import DSBox from "views/system-design/DSBox";
import SimpleSwitch from "../../../../components/Widgets/SimpleSwitch";
import SimpleDropdown from "../../../../components/Widgets/SimpleDropdown";
import {
  CYCLE_MODE,
  VIEW_MODE,
  SettingsSectionProps,
  settingValue,
} from "./constants";

/**
 * Show Klydos settings for a profile — informed by DeviceModal.tsx's Show
 * Klydos section (lines 548-792 at last read), not extracted from it. Three
 * things that section does are deliberately left out here, because they
 * don't have a coherent meaning for a profile applied to many devices:
 *  - The legacy "Show" dropdown (replaced by the three cycle-mode fields in
 *    firmware >= 606) and the "available in version >= 606" footnotes —
 *    always use the modern cycle-mode fields, since we don't know each
 *    assigned device's version.
 *  - Version-gating "Stay in Favorites" (device.version >= 604) — always
 *    shown; an older device just won't apply it (inert, unknown key).
 *  - Display Mode's dynamic "MYKLYDOS" option (only added when a single
 *    device has an assigned user) — a profile has no single device/user to
 *    check, so that option is never offered here.
 */
const ShowKlydosSettings = ({ values, onChange }: SettingsSectionProps) => {
  return (
    <DSBox>
      <DSBox
        title="Switch Fresh Hold on/off"
        sx={{ display: "flex", ml: "20px" }}
      >
        <SimpleSwitch
          title="Fresh Hold"
          on={undefined}
          default={settingValue<boolean>(values, "freshHold")}
          onClick={(on) => onChange("freshHold", on)}
        />
      </DSBox>
      <DSBox
        title="Enable this clock to rate klydos (reviews feed)"
        sx={{ display: "flex", ml: "20px" }}
      >
        <SimpleSwitch
          title="Klydos Rater"
          on={undefined}
          default={settingValue<boolean>(values, "isKlydosRater")}
          onClick={(on) => onChange("isKlydosRater", on)}
        />
      </DSBox>
      <DSBox title="Feed cycle mode: fixed Klydo or changes every hour/day">
        <SimpleDropdown
          title="Feed Cycle Mode: "
          list={CYCLE_MODE}
          default={settingValue<string>(values, "feedCycleMode")}
          onSelect={(s) => onChange("feedCycleMode", s)}
        />
      </DSBox>
      <DSBox title="Collection cycle mode: fixed Klydo or changes every hour/day">
        <SimpleDropdown
          title="Collection Cycle Mode: "
          list={CYCLE_MODE}
          default={settingValue<string>(values, "collectionCycleMode")}
          onSelect={(s) => onChange("collectionCycleMode", s)}
        />
      </DSBox>
      <DSBox title="Favorites cycle mode: fixed Klydo or changes every hour/day">
        <SimpleDropdown
          title="Favorites Cycle Mode: "
          list={CYCLE_MODE}
          default={settingValue<string>(values, "favoritesCycleMode")}
          onSelect={(s) => onChange("favoritesCycleMode", s)}
        />
      </DSBox>
      <DSBox
        title="Return favorited Klydos to the feed after a duration"
        sx={{ display: "flex", ml: "20px" }}
      >
        <SimpleSwitch
          title="Stay in Favorites"
          on={undefined}
          default={settingValue<boolean>(values, "stayInFavorits")}
          onClick={(on) => onChange("stayInFavorits", on)}
        />
      </DSBox>
      <DSBox title="Display mode: all Klydos or device favorites">
        <SimpleDropdown
          title="Display Mode: "
          list={VIEW_MODE}
          default={settingValue<string>(values, "mode")}
          onSelect={(s) => onChange("mode", s)}
        />
      </DSBox>
    </DSBox>
  );
};

export default ShowKlydosSettings;
